import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';
const NAVY = '#0d1b3e';
const GOLD = '#c9a227';

// Shown to free users with an active trialFeaturesEndsAt. Tells them they
// have temporary access to paid pages and links them straight in. Dismissible
// per session (not permanent) so users who've seen it can hide it but it
// reappears on their next session.
export default function TrialBanner() {
  const [info, setInfo] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('scn-trial-banner-dismissed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/stripe/subscription`)
      .then(res => {
        if (cancelled) return;
        const d = res.data;
        // Active trial, original behavior
        if (d.tier === 'free' && d.trialActive && d.trialDaysLeft > 0) {
          setInfo({ kind: 'active', daysLeft: d.trialDaysLeft });
          return;
        }
        // Trial JUST ended (within last 72h, still free), peak conversion moment.
        // Different banner copy, separate dismiss key so users see it even if
        // they dismissed the active-trial banner earlier.
        if (d.tier === 'free' && d.trialJustEnded) {
          setInfo({ kind: 'expired', hoursSince: d.hoursSinceTrialEnd || 0 });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Re-read dismissed flag if banner kind changes (different keys for active vs expired)
  useEffect(() => {
    if (!info) return;
    const key = info.kind === 'expired' ? 'scn-trial-expired-dismissed' : 'scn-trial-banner-dismissed';
    try { setDismissed(sessionStorage.getItem(key) === '1'); } catch {}
  }, [info]);

  if (!info || dismissed) return null;

  const dismiss = () => {
    const key = info.kind === 'expired' ? 'scn-trial-expired-dismissed' : 'scn-trial-banner-dismissed';
    try { sessionStorage.setItem(key, '1'); } catch {}
    setDismissed(true);
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // ─── Trial-expired variant (new) ───────────────────────────────────────────
  if (info.kind === 'expired') {
    const headline = info.hoursSince < 24
      ? 'Your trial just ended'
      : `Your trial ended ${Math.floor(info.hoursSince / 24)} day${Math.floor(info.hoursSince / 24) === 1 ? '' : 's'} ago`;
    if (isMobile) {
      return (
        <div className="scn-trial-banner" style={{
          background: '#7f1d1d', color: 'white', padding: '8px 12px',
          fontFamily: "'Manrope', sans-serif", fontSize: 13,
          position: 'sticky', top: 0, zIndex: 50,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <strong style={{ color: GOLD, fontWeight: 700 }}>{headline}</strong>
            <span style={{ color: 'rgba(255,255,255,0.75)', marginLeft: 6 }}>Keep paid features</span>
          </span>
          <Link to="/settings?tab=subscription" style={{ background: GOLD, color: NAVY, textDecoration: 'none', padding: '6px 14px', borderRadius: 6, fontWeight: 800, fontSize: 12, flexShrink: 0 }}>Upgrade</Link>
          <button onClick={dismiss} aria-label="Dismiss" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 18, padding: '6px 10px', lineHeight: 1, flexShrink: 0, minWidth: 36, minHeight: 36 }}>×</button>
        </div>
      );
    }
    return (
      <div className="scn-trial-banner" style={{
        background: `linear-gradient(135deg, #7f1d1d, ${NAVY})`, color: 'white',
        padding: '10px 18px', fontFamily: "'Manrope', sans-serif", fontSize: 13,
        borderBottom: `2px solid ${GOLD}`, position: 'sticky', top: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      }}>
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong style={{ color: GOLD }}>{headline}</strong>
          <span style={{ color: 'rgba(255,255,255,0.85)' }}>
            Keep Real P&amp;L, Customer CRM, and Grading ROI
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>
          <Link to="/settings?tab=subscription" style={{ background: GOLD, color: NAVY, textDecoration: 'none', padding: '6px 16px', borderRadius: 6, fontWeight: 800, fontSize: 13 }}>Upgrade, cancel anytime</Link>
          <button onClick={dismiss} aria-label="Dismiss" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 16, padding: '6px 10px', lineHeight: 1, minWidth: 36, minHeight: 36 }}>×</button>
        </div>
      </div>
    );
  }

  // ─── Trial-active variant (existing) ───────────────────────────────────────
  const days = info.daysLeft;
  const urgent = days <= 2;

  if (isMobile) {
    return (
      <div className="scn-trial-banner" style={{
        background: urgent ? '#b91c1c' : NAVY,
        color: 'white',
        padding: '8px 12px',
        fontFamily: "'Manrope', sans-serif",
        fontSize: 13,
        position: 'sticky',
        top: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
      }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <strong style={{ color: GOLD, fontWeight: 700 }}>{days} day{days === 1 ? '' : 's'} left</strong>
          <span style={{ color: 'rgba(255,255,255,0.7)', marginLeft: 6 }}>in trial</span>
        </span>
        <Link
          to="/settings?tab=subscription"
          style={{
            background: GOLD, color: NAVY, textDecoration: 'none',
            padding: '6px 14px', borderRadius: 6, fontWeight: 800, fontSize: 12,
            flexShrink: 0,
          }}
        >Upgrade</Link>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 18, padding: '6px 10px', lineHeight: 1, flexShrink: 0, minWidth: 36, minHeight: 36 }}
        >×</button>
      </div>
    );
  }

  return (
    <div className="scn-trial-banner" style={{
      background: urgent ? `linear-gradient(135deg, #b91c1c, ${NAVY})` : `linear-gradient(135deg, ${NAVY}, #1a2a55)`,
      color: 'white',
      padding: '10px 18px',
      fontFamily: "'Manrope', sans-serif",
      fontSize: 13,
      borderBottom: `2px solid ${GOLD}`,
      position: 'sticky',
      top: 0,
      zIndex: 50,
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      flexWrap: 'wrap',
    }}>
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        <strong style={{ color: GOLD }}>
          {days} day{days === 1 ? '' : 's'} left
        </strong>
        <span style={{ color: 'rgba(255,255,255,0.85)' }}>
          in your free trial, try the paid features before they lock
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>
        <Link
          to="/accounting"
          style={{
            background: 'rgba(201,162,39,0.18)', color: GOLD, textDecoration: 'none',
            padding: '5px 11px', borderRadius: 6, fontWeight: 700, fontSize: 12,
            border: '1px solid rgba(201,162,39,0.4)',
          }}
        >Real P&amp;L →</Link>
        <Link
          to="/grading"
          style={{
            background: 'rgba(201,162,39,0.18)', color: GOLD, textDecoration: 'none',
            padding: '5px 11px', borderRadius: 6, fontWeight: 700, fontSize: 12,
            border: '1px solid rgba(201,162,39,0.4)',
          }}
        >Grading ROI →</Link>
        <Link
          to="/customers"
          style={{
            background: 'rgba(201,162,39,0.18)', color: GOLD, textDecoration: 'none',
            padding: '5px 11px', borderRadius: 6, fontWeight: 700, fontSize: 12,
            border: '1px solid rgba(201,162,39,0.4)',
          }}
        >Customer CRM →</Link>
        <Link
          to="/settings?tab=subscription"
          style={{
            background: GOLD, color: NAVY, textDecoration: 'none',
            padding: '5px 11px', borderRadius: 6, fontWeight: 800, fontSize: 12,
          }}
        >Upgrade</Link>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 16, padding: '6px 10px', lineHeight: 1, minWidth: 36, minHeight: 36 }}
        >×</button>
      </div>
    </div>
  );
}
